import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft } from "lucide-react";
import PageContainer from "@/components/PageContainer";
import SoftButton from "@/components/SoftButton";
import {
  QUIZ_QUESTIONS,
  QUIZ_OPTIONS,
  buildCheckInSnapshot,
} from "@/lib/quizData";
import {
  getQuizDraft,
  setQuizDraft,
  clearQuizDraft,
  saveResult,
} from "@/lib/storage";
import { QUIZ } from "@/constants/testIds";
import LogoMark from "@/components/LogoMark";

export default function Quiz() {
  const nav = useNavigate();
  // Lazy-init from localStorage to avoid race between load/persist effects.
  const [step, setStep] = useState(() => {
    const d = getQuizDraft();
    return d?.step ?? 0;
  });
  const [answers, setAnswers] = useState(() => {
    const d = getQuizDraft();
    return d?.answers ?? {};
  });

  // Persist draft on changes.
  useEffect(() => {
    setQuizDraft({ step, answers });
  }, [step, answers]);

  const total = QUIZ_QUESTIONS.length;
  const current = QUIZ_QUESTIONS[step];
  const selected = answers[current.id];
  const progress = useMemo(
    () => Math.round(((step + (selected ? 1 : 0)) / total) * 100),
    [step, selected, total]
  );

  const handleSelect = (val) => {
    setAnswers((a) => ({ ...a, [current.id]: val }));
  };

  const handleNext = () => {
    if (step < total - 1) {
      setStep((s) => s + 1);
      return;
    }
    // Finish: save a private snapshot. No score, rank, diagnosis, or category.
    const snapshot = buildCheckInSnapshot({ ...answers, [current.id]: selected });
    const saved = saveResult(snapshot);
    clearQuizDraft();
    nav(`/results/${saved.id}`, { replace: true });
  };

  const handleBack = () => {
    if (step === 0) {
      nav("/home");
    } else {
      setStep((s) => s - 1);
    }
  };

  const handleStartOver = () => {
    setAnswers({});
    setStep(0);
    clearQuizDraft();
  };

  return (
    <PageContainer testid={QUIZ.root} className="min-h-[100svh] flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          data-testid={QUIZ.back}
          onClick={handleBack}
          aria-label="Back"
          className="flex items-center gap-1.5 px-3 py-2 rounded-full un-press text-xs font-medium"
          style={{
            background: "rgba(74,32,64,0.06)",
            color: "var(--un-text-2)",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          <ChevronLeft size={14} /> Back
        </button>
        <div className="flex flex-col items-center gap-0.5">
          <LogoMark size={28} title="" />
          <p className="text-[10px] tracking-wide" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
            {step + 1} of {total}
          </p>
        </div>
        <button
          data-testid={QUIZ.exit}
          onClick={() => nav("/home")}
          className="text-xs font-medium px-3 py-2 rounded-full un-press"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Exit
        </button>
      </div>

      {/* Progress bar */}
      <div
        className="h-1 rounded-full mb-10 overflow-hidden"
        style={{ background: "rgba(74,32,64,0.08)" }}
        data-testid={QUIZ.progress}
        aria-valuenow={progress}
        role="progressbar"
      >
        <motion.div
          className="h-full"
          style={{ background: "var(--un-plum)" }}
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.32 }}
          className="flex-1 flex flex-col"
        >
          <p
            className="text-[11px] uppercase tracking-[0.3em] mb-3"
            style={{ color: "var(--un-terracotta)", fontFamily: "Manrope, sans-serif" }}
          >
            Check-In
          </p>
          <h2
            data-testid={QUIZ.questionTitle}
            className="text-[28px] sm:text-[32px] leading-[1.16] font-medium tracking-tight mb-3"
            style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
          >
            {current.prompt}
          </h2>
          {current.helper && (
            <p
              className="text-sm mb-8 italic"
              style={{
                color: "var(--un-text-3)",
                fontFamily: "Cormorant Garamond, serif",
                fontSize: "17px",
              }}
            >
              {current.helper}
            </p>
          )}

          {/* Options */}
          <div className="flex flex-col gap-2.5">
            {QUIZ_OPTIONS.map((opt, idx) => {
              const isActive = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`${QUIZ.optionPrefix}${idx}`}
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full text-left px-5 py-4 rounded-2xl un-press transition-all duration-200 ${
                    isActive ? "un-option-active" : ""
                  }`}
                  style={{
                    background: isActive ? "rgba(74,32,64,0.06)" : "#FFFFFF",
                    boxShadow: isActive
                      ? "inset 0 0 0 1.5px var(--un-plum), 0 6px 18px rgba(74,32,64,0.07)"
                      : "0 4px 14px rgba(74,32,64,0.04)",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                  aria-pressed={isActive}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center"
                      style={{
                        background: isActive
                          ? "var(--un-plum)"
                          : "rgba(74,32,64,0.08)",
                      }}
                    >
                      {isActive && (
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: "#FDFBF7" }}
                        />
                      )}
                    </span>
                    <span
                      className="text-[15px] font-medium"
                      style={{ color: "var(--un-text-1)" }}
                    >
                      {opt.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="sticky bottom-0 left-0 right-0 pt-6 pb-6 mt-8">
        <SoftButton
          data-testid={QUIZ.next}
          variant="primary"
          size="lg"
          disabled={!selected}
          onClick={handleNext}
          iconRight={<ArrowRight size={18} />}
          className="w-full"
        >
          {step === total - 1 ? "See my reflection" : "Continue"}
        </SoftButton>
        {Object.keys(answers).length > 0 && (
          <button
            data-testid={QUIZ.startOver}
            onClick={handleStartOver}
            className="block mx-auto mt-3 text-xs font-medium"
            style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
          >
            Start over
          </button>
        )}
      </div>
    </PageContainer>
  );
}
